import React from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import { FavoriteButton } from '@rover/components/src/buttons';
import Rebook from '@rover/icons/svg/rebook.svg';
import Trophy from '@rover/icons/svg/trophy.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import {
  shouldShowRatingsReviews,
  shouldShowRepeatCount,
  shouldShowYearsOfExperience,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';

import RatingsReview from '../../RatingsReview/RatingsReview';

import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

type Props = {
  ratingsAverage: string;
  repeatClientCount: number;
  reviewsCount: number;
  yearsOfExperience: string;
  isAuthenticated: boolean | null | undefined;
  isFavorite: boolean | undefined;
  onFavoriteToggled: () => void;
  hideFavorite: boolean | null | undefined;
};

const ICON_SIZE = 16;

export default function PillsRow({
  ratingsAverage,
  repeatClientCount,
  reviewsCount,
  yearsOfExperience,
  isAuthenticated,
  isFavorite,
  onFavoriteToggled,
  hideFavorite,
}: Props): React.ReactElement {
  const { t } = useLingui();
  const showRatingsReviews = shouldShowRatingsReviews(reviewsCount);
  const showRepeatCount = shouldShowRepeatCount(repeatClientCount);
  const showYearsOfExperience = shouldShowYearsOfExperience(
    yearsOfExperience,
    showRatingsReviews,
    showRepeatCount
  );

  return (
    <Flex width="100%" mt="3x" mb="2x" alignItems="flex-start">
      <Flex
        flex="1"
        flexDirection="row"
        gap="2x"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
      >
        {showRatingsReviews && (
          <Box minWidth="30%">
            <RatingsReview ratingsAverage={ratingsAverage} reviewsCount={reviewsCount} />
          </Box>
        )}
        {showRepeatCount && (
          <Flex flex="1" gap="1x" flexBasis="auto">
            <Rebook width={ICON_SIZE} height={ICON_SIZE} />
            <Text size="100">
              {t({
                message: plural(repeatClientCount, {
                  one: '# repeat client',
                  other: '# repeat clients',
                }),
              })}
            </Text>
          </Flex>
        )}

        {showYearsOfExperience && (
          <Flex flex="1" gap="1x">
            <Trophy width={ICON_SIZE} height={ICON_SIZE} />
            <Text size="100">{yearsOfExperience}</Text>
          </Flex>
        )}
        {!hideFavorite && (
          <Box zIndex={`${SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE}`}>
            <FavoriteButton
              variant="flat"
              on={!!isFavorite}
              onPress={({ webEvent }) => {
                webEvent?.stopPropagation();

                if (isAuthenticated) {
                  onFavoriteToggled();
                } else {
                  // @ts-expect-error
                  window.location = `/account/continue/?action=sign_in&next=${window.location.pathname}`;
                }
              }}
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
}
